(function (factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else factory().mount(document);
})(function () {
  'use strict';

  function simulate(round, strategy) {
    if (!Array.isArray(strategy) || strategy.length !== 3 || strategy.some((n) => n !== 0 && n !== 1)) throw new Error('Choose one answer in each row.');
    const slot = strategy[0] * 4 + strategy[1] * 2 + strategy[2];
    const rateBps = round.rates[slot];
    // The lead pick can change the item itself (the small-bottle round offers
    // the 200 ml bottle); otherwise the round's own item is sold.
    const lead = round.decisions[0].options[strategy[0]];
    const listPriceCents = lead.priceCents === undefined ? round.priceCents : lead.priceCents;
    const itemCostCents = lead.itemCostCents === undefined ? round.itemCostCents : lead.itemCostCents;
    const discountBps = round.decisions[2].options[strategy[2]].discountBps;
    if (!Number.isInteger(rateBps) || rateBps < 0 || rateBps > 10000 || !Number.isInteger(round.recipients) || round.recipients <= 0 || !Number.isInteger(listPriceCents) || listPriceCents <= 0 || !Number.isInteger(discountBps) || discountBps < 0 || discountBps >= 10000) throw new Error('Invalid saved scenario.');
    const orders = Math.round(round.recipients * rateBps / 10000);
    const priceCents = Math.round(listPriceCents * (10000 - discountBps) / 10000);
    const revenueCents = orders * priceCents;
    if (!Number.isInteger(itemCostCents) || itemCostCents < 0) throw new Error('Invalid item cost.');
    const costCents = orders * itemCostCents;
    const contributionCents = revenueCents - costCents;
    return {
      orders, priceCents, revenueCents, itemCostCents, costCents, contributionCents,
      rate: orders / round.recipients
    };
  }

  // Compares a pick with the other option while the other two picks stay put.
  // The rounds keep this in step with best[]: in every combination, the best
  // pick is the one that makes more (inbox-arena-rounds.cjs explains why).
  function reviewChoice(round, strategy, decision) {
    if (![0, 1, 2].includes(decision)) throw new Error('Choose a pick to review.');
    const current = simulate(round, strategy);
    const choice = [...strategy];
    choice[decision] = 1 - choice[decision];
    const result = simulate(round, choice);
    const difference = result.contributionCents - current.contributionCents;
    return {
      choice, current, result, difference,
      differencePercent: current.contributionCents > 0 ? difference / current.contributionCents * 100 : null,
      grade: difference > 0 ? 'The other option makes more' : difference < 0 ? 'This pick makes more' : 'Same result',
      symbol: difference > 0 ? '↑' : difference < 0 ? '✓' : '=',
      direction: difference > 0 ? 'up' : difference < 0 ? 'down' : 'same'
    };
  }

  // A whole-number share, rounded down so that 99.6% of the best never reads
  // as 100%. Cents are integers, so the division is exact enough to floor.
  function shareOf(partCents, wholeCents) {
    return wholeCents > 0 ? Math.max(0, Math.floor(partCents * 100 / wholeCents)) : null;
  }

  // The player's email against the best combination for the same round.
  function scoreRound(round, strategy) {
    const you = simulate(round, strategy), best = simulate(round, round.best);
    const matched = strategy.map((pick, i) => pick === round.best[i]);
    return { you, best, gap: best.contributionCents - you.contributionCents, matched, share: shareOf(you.contributionCents, best.contributionCents) };
  }

  // A margin calculation, independent of the game's authored buyer rates.
  function discountBreakEven(priceCents, itemCostCents, discountBps) {
    if (!Number.isInteger(priceCents) || priceCents <= 0 || !Number.isInteger(itemCostCents) || itemCostCents < 0 || !Number.isInteger(discountBps) || discountBps < 0 || discountBps >= 10000) throw new Error('Invalid discount assumptions.');
    if (discountBps === 0) return null;
    const discountedPriceCents = Math.round(priceCents * (10000 - discountBps) / 10000);
    const fullMarginCents = priceCents - itemCostCents;
    const discountedMarginCents = discountedPriceCents - itemCostCents;
    if (fullMarginCents <= 0) return null;
    return {
      discountedPriceCents, fullMarginCents, discountedMarginCents,
      extraBuyersPercent: discountedMarginCents > 0 ? (fullMarginCents / discountedMarginCents - 1) * 100 : null
    };
  }

  function mount(document) {
    const window = document.defaultView;
    if (!window) return;
    const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    const money = (cents) => currency.format(cents / 100).replace(/\.00$/, '');
    const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
    const percent = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
    const list = (words) => words.length < 3 ? words.join(' and ') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
    document.querySelectorAll('[data-inbox-arena]').forEach((root) => {
      if (root.dataset.arenaReady === 'true') return;
      const $ = (name) => root.querySelector(`[data-arena-${name}]`);
      let rounds;
      try {
        rounds = JSON.parse($('scenarios').textContent);
        if (!Array.isArray(rounds) || rounds.length !== 5) return;
        rounds.forEach(round => {
          if (round.decisions.length !== 3 || round.decisions.some(d => d.options.length !== 2) || round.rates.length !== 8) throw new Error('Invalid round.');
          simulate(round, round.best);
        });
      } catch (_) { return; }
      root.dataset.arenaReady = 'true';
      // Reduced motion is honoured automatically. A separate "Pause motion"
      // button once took the most prominent spot in the phone header, read
      // "Motion on" while motion was off, and paused almost nothing.
      const preference = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: true };
      let quiet = preference.matches, gone = false;
      let index = 0, strategy = [0, 0, 0], picked = new Set(), phase = 'building', results = [];
      let phoneStep = 0, advanceTimer = null;
      let started = false, completed = false, timer = null, editAnimation = null, activeSide = 'you';
      const preview = root.querySelector('.arena-preview');
      const detailsDialog = $('details-dialog');
      let previewVisible = !('IntersectionObserver' in window);
      const text = (name, value) => { $(name).textContent = value; };
      function node(tag, className, content) {
        const element = document.createElement(tag);
        if (className) element.className = className;
        if (content !== undefined) element.textContent = content;
        return element;
      }
      function track(name) { document.dispatchEvent(new window.CustomEvent('myuser:tool-event', { detail: { name } })); }
      function pane(name) {
        ['choices', 'results', 'final'].forEach(key => {
          const element = $(key), active = key === name;
          element.dataset.active = String(active);
          element.setAttribute('aria-hidden', String(!active));
          element.toggleAttribute('inert', !active);
        });
      }
      // Phones show one decision at a time (mobile-tools.css). The step
      // controls are absent from the compact homepage version.
      const stepMode = () => !!$('step-nav') && window.innerWidth <= 650;
      function stopEdit() {
        if (!editAnimation) return;
        editAnimation.onfinish = null;
        editAnimation.cancel();
        editAnimation = null;
      }
      function clearAdvance() { if (advanceTimer !== null) window.clearTimeout(advanceTimer); advanceTimer = null; }
      function resetFeedback(note = 'Each pick changes one part of this email.') {
        stopEdit();
        preview.dataset.arenaDraftFocus = 'none';
        preview.dataset.arenaEditTarget = 'none';
        text('edit-number', ''); text('edit-note', note);
      }
      function showEdit(decision, note, animate = false) {
        const current = rounds[index].decisions[decision];
        const target = current.previewTarget || ['message', 'timing', 'offer'][decision];
        preview.dataset.arenaDraftFocus = String(decision);
        preview.dataset.arenaEditTarget = target;
        text('edit-number', `0${decision + 1}`); text('edit-note', note);
        stopEdit();
        if (!animate || quiet || gone || document.hidden || !previewVisible) return;
        const element = target === 'message' ? root.querySelector('.arena-email-copy')
          : $(target === 'timing' ? 'draft-timing' : target === 'button' ? 'draft-cta' : 'draft-offer');
        if (!element?.animate) return;
        const animation = element.animate([
          { opacity: .65, transform: 'translateY(3px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
        editAnimation = animation;
        animation.onfinish = () => {
          if (editAnimation === animation) editAnimation = null;
          animation.onfinish = null;
          animation.cancel();
        };
      }
      function clearReveal() { if (timer !== null) window.clearTimeout(timer); timer = null; }
      function mobileView(view = 'picks') {
        root.dataset.arenaMobileView = view;
        root.querySelectorAll('[data-arena-mobile-view]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.arenaMobileView === view));
        });
      }
      function revealPhoneSection(target) {
        if (window.innerWidth > 650 || document.hidden || gone) return;
        // A phone's panels grow with their content. After a deliberate round
        // transition, show the new information below the sticky navigation.
        const headerBottom = document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0;
        const scrollPadding = parseFloat(window.getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        const top = window.scrollY + target.getBoundingClientRect().top - Math.max(headerBottom, scrollPadding, 0) - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: quiet ? 'instant' : 'smooth' });
      }
      function totals() {
        const sum = key => results.reduce((total, result) => total + result[key].contributionCents, 0);
        const you = sum('you'), best = sum('best');
        return { you, best, share: shareOf(you, best), matched: results.reduce((total, result) => total + result.matched.filter(Boolean).length, 0) };
      }
      function scoreboard() {
        const score = totals();
        $('total-box').hidden = results.length === 0;
        text('total', money(score.you));
        text('total-detail', results.length ? `of ${money(score.best)} possible · ${score.share}%` : '');
        [...$('history').children].forEach((mark, i) => {
          const result = results[i];
          const best = result && result.gap === 0;
          mark.textContent = result ? best ? '✓' : `${result.share}%` : String(i + 1);
          mark.className = result ? best ? 'is-best' : 'is-missed' : i === index && phase !== 'complete' ? 'is-current' : '';
          mark.setAttribute('aria-label', `Round ${i + 1}: ${result ? best ? `best possible result, ${money(result.you.contributionCents)}` : `${money(result.you.contributionCents)} of ${money(result.best.contributionCents)} possible` : i === index ? 'playing now' : 'not played'}`);
        });
        return score;
      }
      // Which pick fills the big button: the button pick when it names one
      // (free-guide and refill rounds), otherwise the lead pick.
      const buttonDecision = round => round.decisions[2].options.some(option => option.preview?.cta) ? 2 : 0;
      // Picks that change the price: a discount, or a different bottle.
      const priceDecisions = round => [0, 2].filter(d => round.decisions[d].options.some(option => option.priceCents !== undefined || d === 2 && option.discountBps > 0));
      // A note pick (small-bottle round) changes only the product note.
      const noteDecision = round => round.decisions[2].previewTarget === 'offer' && !priceDecisions(round).includes(2);
      const placeholder = (round, decision) => `Pick ${decision + 1} · ${round.decisions[decision].shortLabel}`;
      function offerLine(round, choice, has) {
        const third = round.decisions[2].options[choice[2]];
        return third.lines && has(0) ? third.lines[choice[0]] : third.line;
      }
      function part(name, value, empty) {
        const element = $(name);
        element.textContent = value;
        element.toggleAttribute('data-arena-placeholder', empty);
      }
      function renderEmail(side = 'you') {
        activeSide = side;
        const round = rounds[index];
        const scored = side === 'best' || phase !== 'building';
        const choice = side === 'best' ? round.best : strategy;
        // Until a pick is made, its part of the email stays blank. The first
        // version filled the whole preview with round 1's winning answers
        // before the player had chosen anything.
        const has = decision => scored || picked.has(decision);
        const empty = !scored && picked.size === 0;
        preview.dataset.arenaEmpty = String(empty);
        $('preview-empty').hidden = !empty;
        const lead = has(0) ? round.decisions[0].options[choice[0]] : null;
        const third = has(2) ? round.decisions[2].options[choice[2]] : null;
        const message = Object.assign({}, lead || {}, third?.preview || {});
        part('draft-subject', lead ? message.subject : placeholder(round, 0), !lead);
        part('draft-headline', lead ? message.subject : placeholder(round, 0), !lead);
        part('draft-body', lead ? message.body : '', false); // empty: hidden by :empty
        const ctaFrom = buttonDecision(round);
        part('draft-cta', has(ctaFrom) ? `${message.cta} →` : `Button · Pick ${ctaFrom + 1}`, !has(ctaFrom));
        text('draft-secondary', third?.preview?.secondaryCta || '');
        $('draft-secondary').hidden = !third?.preview?.secondaryCta;
        part('draft-offer', third ? offerLine(round, choice, has) : placeholder(round, 2), !third);
        const timing = has(1) ? round.decisions[1].options[choice[1]].label : placeholder(round, 1);
        part('draft-timing', timing, !has(1));
        const photo = $('draft-image');
        const paper = photo.closest('.arena-email-paper');
        photo.hidden = !lead;
        paper.dataset.arenaImageLayout = lead ? lead.image.layout : 'none';
        if (lead) {
          const image = lead.image;
          photo.srcset = image.srcset; photo.src = image.src; photo.alt = image.alt; photo.width = image.width; photo.height = image.height;
          photo.style.objectPosition = image.position; photo.style.objectFit = image.fit; photo.style.background = image.background;
        }
        const priced = priceDecisions(round).every(has);
        const result = simulate(round, choice);
        part('draft-price', priced ? money(result.priceCents) : '—', !priced);
        text('draft-kept', priced ? `${money(result.priceCents - result.itemCostCents)} made per sale after item costs` : `Pick ${priceDecisions(round).map(d => d + 1).join(' and ')} sets the price`);
        root.querySelectorAll('[data-arena-email-side]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.arenaEmailSide === side)));
        // The tab already names whose email this is; the label adds the money.
        text('preview-state', !scored ? empty ? 'Draft · no picks yet' : `Draft · ${picked.size} of 3 picks` : `Made ${money(result.contributionCents)}`);
        if (side === 'you' && !scored) renderStrip(round, message, lead, has, ctaFrom, priced ? money(result.priceCents) : null);
      }
      // The phone summary above the answers, so each pick visibly changes
      // the email while the full preview sits behind the other tab.
      function renderStrip(round, message, lead, has, ctaFrom, price) {
        if (!$('strip')) return;
        // Short slots: an icon marks each part and the step buttons below
        // name the picks.
        const slot = decision => `Pick ${decision + 1}`;
        part('strip-subject', lead ? message.subject : placeholder(round, 0), !lead);
        part('strip-time', has(1) ? round.decisions[1].options[strategy[1]].label : slot(1), !has(1));
        part('strip-cta', has(ctaFrom) ? message.cta : slot(ctaFrom), !has(ctaFrom));
        const priceFrom = priceDecisions(round);
        part('strip-price', price || slot(priceFrom[priceFrom.length - 1] ?? 2), !price);
        const note = noteDecision(round);
        $('strip-note-row').hidden = !note;
        part('strip-note', !note ? '' : has(2) ? offerLine(round, strategy, has) : placeholder(round, 2), note && !has(2));
      }
      function markStrip(decision) {
        if (!$('strip')) return;
        const round = rounds[index], target = round.decisions[decision].previewTarget;
        $('strip').dataset.arenaStripChanged = decision === 1 ? 'time' : target === 'button' ? 'cta' : target === 'message' ? 'subject' : priceDecisions(round).includes(decision) ? 'price' : 'note';
      }
      function updatePhoneSteps() {
        const navigation = $('step-nav');
        if (!navigation) return;
        navigation.hidden = false;
        $('step-actions').hidden = false;
        root.dataset.arenaStep = String(phoneStep);
        [...$('decisions').children].forEach((field, i) => {
          field.dataset.phoneCurrent = String(i === phoneStep);
        });
        navigation.querySelectorAll('[data-arena-step]').forEach((button, i) => {
          button.setAttribute('aria-pressed', String(i === phoneStep));
          button.setAttribute('aria-label', `Pick ${i + 1}: ${rounds[index].decisions[i].shortLabel}${picked.has(i) ? ', answered' : ''}`);
          button.querySelector('strong').textContent = rounds[index].decisions[i].shortLabel;
          button.querySelector('[data-arena-step-mark]').textContent = picked.has(i) ? '✓' : String(i + 1);
        });
        $('step-back').disabled = phoneStep === 0;
        // The last step plays the round from here, right under its answers;
        // the old "Play" button sat below the fold after the third pick.
        const last = phoneStep === 2, next = $('step-next');
        next.dataset.arenaStepPlay = String(last);
        next.firstChild.textContent = last ? 'Play this round ' : 'Next pick ';
        next.disabled = last ? phase !== 'building' || picked.size !== 3 : !picked.has(phoneStep);
      }
      function goToPhoneStep(step, focus = 'step') {
        if (phase !== 'building' || ![0, 1, 2].includes(step)) return;
        clearAdvance();
        phoneStep = step;
        updatePhoneSteps();
        if (focus === 'field') {
          // After a pick moves on, the tapped answer is gone; continue from
          // the new question rather than dropping focus to the page.
          $('decisions').children[step]?.focus({ preventScroll: true });
          return;
        }
        // Keep focus on the step control; the selected field is next in the
        // reading order.
        root.querySelector(`[data-arena-step="${step}"]`)?.focus({ preventScroll: true });
        revealPhoneSection($('choices'));
      }
      function setChoices() {
        [...$('decisions').children].forEach((field, decision) => {
          field.dataset.complete = String(picked.has(decision));
        });
        root.querySelectorAll('[data-arena-decision]').forEach(button => {
          const decision = Number(button.dataset.arenaDecision);
          button.disabled = phase !== 'building';
          button.setAttribute('aria-pressed', String(picked.has(decision) && strategy[decision] === Number(button.dataset.arenaPick)));
        });
        $('lock').disabled = phase !== 'building' || picked.size !== 3;
        text('pick-count', `${picked.size} of 3`);
        updatePhoneSteps();
        if (phase === 'building') {
          text('action-title', picked.size === 3 ? 'Ready. Play this round.' : picked.size === 0 ? 'Pick one answer in each row.' : `Pick ${3 - picked.size} more ${picked.size === 2 ? 'answer' : 'answers'}.`);
          renderEmail();
        }
      }
      function buildChoices() {
        const round = rounds[index];
        $('decisions').replaceChildren(...round.decisions.map((decision, i) => {
          const field = node('fieldset', 'arena-decision'); field.tabIndex = -1;
          const legend = node('legend'); legend.append(node('span', '', `0${i + 1}`), document.createTextNode(decision.label));
          const options = node('div', 'arena-switch');
          decision.options.forEach((option, pick) => {
            const button = node('button'); button.type = 'button'; button.dataset.arenaDecision = String(i); button.dataset.arenaPick = String(pick);
            const mark = node('i', 'arena-pick-mark'); mark.setAttribute('aria-hidden', 'true');
            button.append(mark, node('strong', '', option.label), node('span', '', option.detail));
            options.append(button);
          });
          field.append(legend, options); return field;
        }));
        setChoices();
      }
      function inspectDecision(decision, announce = false) {
        if (phase !== 'result') return;
        const round = rounds[index], current = round.decisions[decision], check = reviewChoice(round, strategy, decision);
        root.querySelectorAll('[data-arena-inspect]').forEach(button => {
          const i = Number(button.dataset.arenaInspect), rating = reviewChoice(round, strategy, i);
          const best = strategy[i] === round.best[i];
          const label = round.decisions[i].shortLabel;
          // ✓ only for the best email's pick; a missed pick shows what the
          // other option would add with the other two picks unchanged.
          const grade = best ? 'Best pick' : rating.difference > 0 ? `+${money(rating.difference)} possible` : 'Not the best pick';
          button.setAttribute('aria-pressed', String(i === decision));
          button.dataset.grade = best ? 'best' : 'missed';
          button.querySelector('strong').textContent = label;
          button.querySelector('small').textContent = grade;
          button.querySelector('i').textContent = best ? '✓' : '↑';
          button.setAttribute('aria-label', `${label}: ${grade}. Compare both options`);
        });
        $('choice-detail').dataset.correct = String(strategy[decision] === round.best[decision]);
        text('review-title', current.options[round.best[decision]].label);
        text('review-note', round.notes[decision]);
        for (const [side, pick, score] of [['you', strategy[decision], check.current], ['other', check.choice[decision], check.result]]) {
          text(`comparison-${side}-label`, current.options[pick].label);
          text(`comparison-${side}-amount`, money(score.contributionCents));
          text(`comparison-${side}-rate`, `${number.format(score.rate * 100)}% purchase rate`);
          text(`comparison-${side}-math`, `${score.orders} buyers × ${money(score.priceCents - score.itemCostCents)}`);
          const best = pick === round.best[decision];
          $(`comparison-${side}`).dataset.best = String(best);
          $(`comparison-${side}-best`).hidden = !best;
        }
        const evidence = round.evidence?.[decision];
        $('evidence').open = false; $('evidence').hidden = !evidence;
        if (evidence) {
          ['kind', 'title', 'summary'].forEach(key => text('evidence-' + key, evidence[key]));
          text('evidence-source', evidence.sourceLabel);
          for (const [name, href] of [['source', evidence.url], ['guide', evidence.guide]]) {
            const link = $('evidence-' + name);
            link.hidden = !href || name === 'guide' && href === evidence.url;
            link.setAttribute('href', href || '/blog/inbox-arena-research');
          }
        }
        // A published study sits beside the one decision it tested. The first
        // version showed the cart-timing study beside the discount as well.
        renderStudy(round.study && round.study.decision === decision ? round.study : null);
        const discounts = current.options.map(option => option.discountBps);
        const discountBps = discounts.includes(0) && discounts[0] !== discounts[1] ? Math.max(...discounts) : 0;
        const discount = discountBps > 0 ? discountBreakEven(round.priceCents, round.itemCostCents, discountBps) : null;
        $('discount-math').hidden = !discount;
        if (discount) {
          const offer = `At ${number.format(discountBps / 100)}% off: `;
          text('discount-title', discount.extraBuyersPercent === null
            ? 'Extra buyers cannot close the gap'
            : `About ${number.format(discount.extraBuyersPercent)}% more buyers needed`);
          text('discount-detail', discount.extraBuyersPercent === null
            ? `${offer}${money(discount.discountedPriceCents)} − ${money(round.itemCostCents)} item cost = ${money(discount.discountedMarginCents)} per sale. With no positive amount after item costs, more buyers cannot match full-price sales.`
            : `To match full-price sales after item costs. ${offer}${money(discount.fullMarginCents)} ÷ ${money(discount.discountedMarginCents)} − 1, using a ${money(round.priceCents)} price and ${money(round.itemCostCents)} item cost. This is a cost calculation, not a demand forecast.`);
        }
        const share = check.differencePercent === null ? '' : ` (${percent.format(Math.abs(check.differencePercent))}%)`;
        text('whatif', check.difference === 0 ? 'Either option makes the same money.' : `Switching would make ${money(Math.abs(check.difference))} ${check.difference > 0 ? 'more' : 'less'}${share}.`);
        $('whatif').dataset.direction = check.direction;
        // The review describes the player's saved choice, so the nearby email
        // must show that choice even if the best email was open before.
        if (activeSide !== 'you') renderEmail('you');
        const chosen = current.options[strategy[decision]];
        showEdit(decision, chosen.feedback || chosen.label, announce);
        if (announce) text('announcement', `${current.shortLabel}: ${$('review-note').textContent} ${$('whatif').textContent}`);
      }
      function renderStudy(study) {
        const values = [study?.baseline?.value, study?.treatment?.value];
        const visible = !!study && values.every(value => Number.isFinite(value) && value >= 0);
        $('study').hidden = !visible;
        ['eyebrow', 'metric', 'label', 'intervention', 'context'].forEach(key => text('study-' + key, visible ? study[key] : ''));
        const maximum = visible ? Math.max(...values) : 0;
        ['baseline', 'treatment'].forEach(side => {
          text(`study-${side}-label`, visible ? study[side].label : '');
          text(`study-${side}-value`, visible ? study[side].display : '');
          const width = visible && maximum > 0 ? study[side].value / maximum * 100 : 0;
          $(`study-${side}-bar`).style.width = `${Math.min(100, Math.max(0, width))}%`;
        });
        text('study-source', visible ? study.sourceLabel : '');
        for (const [name, href] of [['source', study?.url], ['guide', study?.guide]]) {
          const link = $('study-' + name);
          link.hidden = !visible || !href;
          link.setAttribute('href', visible && href ? href : '/blog/inbox-arena-research');
        }
      }
      function reveal() {
        if (phase !== 'revealing') return;
        const focusStayed = document.activeElement === $('reveal');
        clearReveal(); phase = 'result'; root.dataset.arenaPhase = phase; root.setAttribute('aria-busy', 'false');
        mobileView(); text('mobile-label', 'Result');
        const round = rounds[index], score = scoreRound(round, strategy);
        const { you, best } = score;
        results.push({ ...score, strategy: [...strategy] }); scoreboard();
        const perfect = score.matched.every(Boolean);
        $('reveal').hidden = true; pane('results'); $('results').dataset.outcome = perfect ? 'best' : 'missed';
        text('result-kicker', `ROUND ${index + 1} · RESULT`);
        text('outcome', perfect ? 'You found the best email.' : `You made ${score.share}% of the best.`);
        const missed = round.decisions.filter((_, i) => !score.matched[i]).map(decision => decision.noun || decision.shortLabel.toLowerCase());
        text('margin', perfect ? `No other set of picks makes more than ${money(best.contributionCents)} here.` : `+${money(score.gap)} was possible with a different ${list(missed)}.`);
        [['you', you, strategy], ['best', best, round.best]].forEach(([side, result, picks]) => {
          const rateBps = round.rates[picks[0] * 4 + picks[1] * 2 + picks[2]];
          text(`${side}-contribution`, money(result.contributionCents));
          text(`${side}-math`, `${result.orders} buyers × ${money(result.priceCents - result.itemCostCents)} after item costs`);
          text(`${side}-calculation`, `${number.format(round.recipients)} shoppers × ${number.format(rateBps / 100)}% preset rate → ${result.orders} buyers (rounded). ${result.orders} × (${money(result.priceCents)} sale price − ${money(result.itemCostCents)} item cost) = ${money(result.contributionCents)} after item costs.`);
          $(`${side}-bar`).style.width = `${Math.max(0, result.contributionCents) / Math.max(you.contributionCents, best.contributionCents, 1) * 100}%`;
        });
        const firstMiss = score.matched.indexOf(false);
        inspectDecision(firstMiss === -1 ? round.lessonDecision ?? 0 : firstMiss);
        $('lock').hidden = true; $('next').hidden = false;
        text('next', index === rounds.length - 1 ? 'See your final score →' : 'Next round →');
        // Each round's lesson sits under its result, beside the next step.
        text('action-title', `Round ${index + 1} lesson`);
        text('action-note', round.lesson);
        root.querySelector('[data-arena-email-side="best"]').hidden = false;
        renderEmail();
        text('announcement', `${$('outcome').textContent} ${$('margin').textContent} Your email made ${money(you.contributionCents)}. The best email made ${money(best.contributionCents)}.`);
        if (focusStayed && !document.hidden && !gone) {
          $('outcome').focus({ preventScroll: true });
          revealPhoneSection($('results'));
        }
      }
      function lock() {
        if (phase !== 'building' || picked.size !== 3) return;
        stopEdit(); clearAdvance();
        phase = 'revealing'; root.dataset.arenaPhase = phase; root.setAttribute('aria-busy', 'true');
        mobileView();
        if (!started) { started = true; track('inbox_arena_started'); }
        setChoices(); $('choices').setAttribute('inert', ''); $('reveal').hidden = false;
        text('action-title', 'Scoring your email…'); text('action-note', $('reveal-audience').textContent);
        $('reveal').focus({ preventScroll: true });
        if (quiet || gone || document.hidden) reveal(); else timer = window.setTimeout(reveal, 650);
      }
      function startRound(moveFocus = false) {
        clearReveal(); clearAdvance(); stopEdit();
        phase = 'building'; strategy = [0, 0, 0]; picked = new Set(); phoneStep = 0;
        if (detailsDialog.open) detailsDialog.close();
        renderStudy(null);
        $('calculation').open = false; $('evidence').open = false; $('evidence').hidden = true;
        $('discount-math').hidden = true; text('discount-title', ''); text('discount-detail', '');
        mobileView(); text('mobile-label', 'Picks');
        root.dataset.arenaPhase = phase; root.setAttribute('aria-busy', 'false');
        pane('choices'); $('reveal').hidden = true;
        $('lock').hidden = false; $('next').hidden = true; $('replay').hidden = true;
        root.querySelector('[data-arena-email-side="best"]').hidden = true;
        const round = rounds[index];
        text('round', `ROUND ${index + 1} OF ${rounds.length}`); text('title', round.title); text('brief', round.brief); text('topic', round.topic);
        // SVG elements have no .hidden property; set the attribute itself.
        root.querySelectorAll('[data-arena-icon]').forEach(icon => icon.toggleAttribute('hidden', icon.getAttribute('data-arena-icon') !== round.icon));
        $('clues').replaceChildren(...round.facts.map(fact => node('li', '', fact)));
        const audience = new Intl.NumberFormat('en-US').format(round.recipients);
        text('audience', `${audience} shoppers`);
        text('reveal-audience', `Same ${audience} shoppers, same costs.`);
        const nouns = round.decisions.map(decision => decision.noun || decision.shortLabel.toLowerCase());
        text('preview-empty-hint', `Pick 1 sets the ${nouns[0]}, pick 2 the ${nouns[1]} and pick 3 the ${nouns[2]}.`);
        text('action-note', 'Your email updates as you pick.');
        text('outcome', 'Your round result'); text('margin', ''); text('review-note', ''); text('whatif', '');
        ['you', 'best'].forEach(side => { $(`${side}-bar`).style.width = '0%'; text(`${side}-contribution`, '$0'); text(`${side}-math`, ''); text(`${side}-calculation`, ''); });
        if ($('strip')) $('strip').dataset.arenaStripChanged = 'none';
        root.dataset.arenaLastPick = 'none'; resetFeedback();
        buildChoices(); scoreboard();
        if (moveFocus) {
          $('title').focus({ preventScroll: true });
          revealPhoneSection(root.querySelector('.arena-brief'));
          text('announcement', `Round ${index + 1}. ${round.title}`);
        }
      }
      function finish() {
        if (phase !== 'result' || index !== rounds.length - 1 || completed) return;
        stopEdit(); completed = true; phase = 'complete'; root.dataset.arenaPhase = phase;
        mobileView(); text('mobile-label', 'Final score');
        pane('final'); $('next').hidden = true; $('replay').hidden = false;
        text('round', 'FINAL SCORE');
        const score = scoreboard(), picks = results.length * 3;
        const perfect = score.matched === picks;
        text('final-title', perfect ? 'You found the best email in all five rounds.' : `You made ${money(score.you)} of ${money(score.best)} possible.`);
        text('final-copy', perfect ? `${picks} of ${picks} picks matched the best: ${money(score.you)}, the most this game allows.` : `${score.share}% of the best result · ${score.matched} of ${picks} picks matched the best.`);
        const row = (label, you, best, share, className) => {
          const tr = node('tr', className); const heading = node('th', '', label); heading.scope = 'row';
          tr.append(heading, node('td', '', money(you)), node('td', '', money(best)), node('td', '', `${share}%`)); return tr;
        };
        $('match-history').replaceChildren(...results.map((result, i) => row(rounds[i].recapLabel, result.you.contributionCents, result.best.contributionCents, result.share, result.gap === 0 ? 'is-best' : '')));
        $('match-total').replaceChildren(row('Total', score.you, score.best, score.share, perfect ? 'is-best' : ''));
        text('action-title', 'That’s all five rounds.'); text('action-note', 'Play again with new picks, or save your notes to use later.');
        text('announcement', `${$('final-title').textContent} ${$('final-copy').textContent}`);
        $('final-title').focus({ preventScroll: true });
        revealPhoneSection($('final'));
        track('inbox_arena_completed');
      }
      function motion() {
        quiet = preference.matches;
        root.classList.toggle('arena-quiet', quiet);
        if (quiet) { stopEdit(); if (phase === 'revealing') reveal(); }
      }
      $('decisions').addEventListener('click', event => {
        const button = event.target.closest('[data-arena-decision]');
        if (!button || phase !== 'building') return;
        const decision = Number(button.dataset.arenaDecision), pick = Number(button.dataset.arenaPick);
        if (![0, 1, 2].includes(decision) || ![0, 1].includes(pick)) return;
        const changed = !picked.has(decision) || strategy[decision] !== pick;
        strategy[decision] = pick; picked.add(decision); setChoices();
        root.dataset.arenaLastPick = String(decision);
        const option = rounds[index].decisions[decision].options[pick];
        showEdit(decision, option.feedback || option.label, changed);
        markStrip(decision);
        text('action-note', `${option.label}. ${picked.size === 3 ? 'All three picks are set.' : 'See it in your email.'}`);
        // On a phone, a pick moves on to the next question after a beat, so
        // the answer visibly lands first. Back and the step buttons still let
        // people revisit any answer. The old flow needed seven taps a round.
        if (stepMode() && decision === phoneStep && phoneStep < 2) {
          clearAdvance();
          advanceTimer = window.setTimeout(() => {
            advanceTimer = null;
            if (phase === 'building' && phoneStep === decision) goToPhoneStep(decision + 1, 'field');
          }, 350);
        }
        // The third answer completes the email: bring "Play this round" fully
        // into view when it sits just under the fold, and only then.
        if (stepMode() && phoneStep === 2 && picked.size === 3 && !document.hidden && !gone) {
          const bottom = $('step-next').getBoundingClientRect().bottom + 12 - window.innerHeight;
          if (bottom > 0) window.scrollTo({ top: window.scrollY + bottom, behavior: quiet ? 'instant' : 'smooth' });
        }
      });
      $('step-nav')?.addEventListener('click', event => {
        const button = event.target.closest('[data-arena-step]');
        if (button) goToPhoneStep(Number(button.dataset.arenaStep));
      });
      $('step-back')?.addEventListener('click', () => goToPhoneStep(phoneStep - 1));
      $('step-next')?.addEventListener('click', () => {
        if (phoneStep === 2) lock();
        else if (picked.has(phoneStep)) goToPhoneStep(phoneStep + 1);
      });
      root.querySelectorAll('[data-arena-inspect]').forEach(button => button.addEventListener('click', () => inspectDecision(Number(button.dataset.arenaInspect), true)));
      root.querySelectorAll('[data-arena-email-side]').forEach(button => button.addEventListener('click', () => {
        const side = button.dataset.arenaEmailSide;
        if (side === 'best' && !['result', 'complete'].includes(phase)) return;
        renderEmail(side); resetFeedback(side === 'best' ? 'The best picks for this round, shown in the email.' : 'Your saved picks, shown in the email.');
      }));
      root.querySelectorAll('button[data-arena-mobile-view]').forEach(button => button.addEventListener('click', () => {
        mobileView(button.dataset.arenaMobileView);
      }));
      $('lock').addEventListener('click', lock);
      $('details-open').addEventListener('click', () => {
        if (phase !== 'result' || detailsDialog.open) return;
        $('calculation').open = true;
        $('evidence').open = !$('evidence').hidden;
        detailsDialog.showModal();
      });
      $('details-close').addEventListener('click', () => detailsDialog.close());
      detailsDialog.addEventListener('click', event => {
        if (event.target === detailsDialog) detailsDialog.close();
      });
      detailsDialog.addEventListener('close', () => {
        if (phase === 'result') $('details-open').focus({ preventScroll: true });
      });
      $('next').addEventListener('click', () => { if (phase === 'result') { if (index === rounds.length - 1) finish(); else { index++; startRound(true); } } });
      $('replay').addEventListener('click', () => {
        if (phase !== 'complete') return;
        index = 0; results = []; started = false; completed = false; startRound(true);
      });
      $('save-notes').addEventListener('click', () => {
        if (phase !== 'complete') return;
        const score = totals();
        const labels = (round, picks) => picks.map((pick, d) => round.decisions[d].options[pick].label).join(' / ');
        const content = ['Inbox Arena — my game notes', 'Made-up practice rounds, not a sales forecast. Score = money after item costs, from 1,000 shoppers a round.', ...results.map((result, i) => [
          `\nRound ${i + 1}: ${rounds[i].title}`,
          `My picks: ${labels(rounds[i], result.strategy)}`,
          `Best picks: ${labels(rounds[i], rounds[i].best)}`,
          `Money after item costs: my email ${money(result.you.contributionCents)}; best email ${money(result.best.contributionCents)} (${result.share}%).`,
          `Lesson: ${rounds[i].lesson}`
        ].join('\n')), `\nTotal: ${money(score.you)} of ${money(score.best)} possible (${score.share}%). ${score.matched} of ${results.length * 3} picks matched the best.`].join('\n');
        const url = window.URL.createObjectURL(new window.Blob([content], { type: 'text/plain' }));
        const link = node('a'); link.href = url; link.download = 'my-inbox-arena-notes.txt'; link.click();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        text('announcement', 'Your game notes are ready to save.');
      });
      preference.addEventListener?.('change', motion);
      if ('IntersectionObserver' in window) {
        new window.IntersectionObserver(entries => {
          previewVisible = entries.some(entry => entry.isIntersecting);
          if (!previewVisible) stopEdit();
        }).observe(preview);
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) { stopEdit(); if (phase === 'revealing') reveal(); } });
      window.addEventListener('pagehide', () => { gone = true; stopEdit(); clearAdvance(); if (phase === 'revealing') reveal(); clearReveal(); });
      window.addEventListener('pageshow', () => { gone = false; });
      startRound(); motion();
    });
  }
  return { simulate, reviewChoice, scoreRound, shareOf, discountBreakEven, mount };
});
